import DialogTitle from '@mui/material/DialogTitle';
import {
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    InputAdornment,
    OutlinedInput,
    Stack,
    Typography,
} from '@mui/material';
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import SearchResult from '../search/SearchResult';
import Pages from '../../pages/cms/Pages';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BindableDataChoiceResponse, PageListItem } from '../../api/types/cms';
import useApi from '../../api';
import { useTranslation } from '../../contexts/TranslationContext';
import { ResultObjectItem } from '../../api/types/search';

type CmsSelectPageDialogProps = {
    open: boolean;
    locale: string;
    value?: PageListItem;
    onClose?: () => void;
    onChange?: (newValue: PageListItem) => void;
};

export default function CmsSelectPageDialog({
    open,
    value,
    locale,
    onClose = () => {},
    onChange = () => {},
}: CmsSelectPageDialogProps) {
    const placeHolderRef = useRef<HTMLElement>();
    const { translate } = useTranslation();
    const { cms } = useApi();

    const [selectedPage, setSelectedPage] = useState<PageListItem | undefined>(
        value,
    );
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const [dataChoices, setDataChoices] = useState<BindableDataChoiceResponse>({
        limit: 100,
        offset: 0,
        data: [],
        has_next: true,
        length: 0,
        max_length: 0,
    });

    const loadSelectionItems = (
        newLimit: number = 100,
        newOffset: number = 0,
    ) => {
        return new Promise(resolve => {
            if (
                selectedPage &&
                selectedPage.has_bonded_data &&
                !loading &&
                searchQuery.length > 0 &&
                (dataChoices.has_next || newOffset === 0)
            ) {
                setLoading(true);
                cms.getPageDataChoiceList(
                    selectedPage.id,
                    searchQuery,
                    newLimit,
                    newOffset,
                )
                    .then(response => {
                        if (response && !response.error) {
                            if (response.offset === 0) {
                                setDataChoices(response);
                            } else {
                                setDataChoices(draft => {
                                    return {
                                        ...draft,
                                        ...response,
                                        data: {
                                            ...draft.data,
                                            ...response.data,
                                        },
                                    };
                                });
                            }
                        }
                        setLoading(false);
                        resolve(null);
                        return;
                    })
                    .catch(() => setLoading(false));
            } else {
                resolve(null);
            }
        });
    };

    useEffect(() => {
        if (selectedPage && selectedPage.has_bonded_data) {
            loadSelectionItems();
        }
    }, [selectedPage, searchQuery]);

    const handleClose = () => {
        setSelectedPage(value ?? undefined);
        onClose();
    };

    const selectAndClose = () => {
        if (selectedPage) {
            onChange(selectedPage);
        }
        handleClose();
    };

    const varName = selectedPage?.slug?.match(/\{(.*)\}/g)?.[0] ?? 'id';

    const onBindingChange = (selectedBinding: ResultObjectItem) => {
        if (
            selectedBinding &&
            selectedPage &&
            selectedPage.has_bonded_data &&
            selectedPage?.bind_value !== selectedBinding[varName]
        ) {
            setSelectedPage({
                ...selectedPage,
                bind_value: selectedBinding[varName],
            });
        }
    };

    return useMemo(() => {
        return (
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title" variant="h5">
                    {'Seite auswählen'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        {selectedPage && (
                            <Card variant="outlined" sx={{ px: 2, py: 1 }}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={2}
                                    minHeight={'8rem'}>
                                    <Typography variant="overline">
                                        {translate('form.general.selection')}
                                    </Typography>
                                    <Card
                                        sx={{
                                            px: 2,
                                            py: 1.5,
                                            width: 'fit-content',
                                            minWidth: 250,
                                        }}>
                                        <Stack spacing={0.5}>
                                            <Typography variant="subtitle1">
                                                {selectedPage?.title}
                                            </Typography>
                                            <Typography variant="subtitle2">
                                                {selectedPage.bind_value
                                                    ? selectedPage?.slug.replace(
                                                          /\{.*\}/g,
                                                          selectedPage.bind_value ??
                                                              '',
                                                      )
                                                    : selectedPage?.slug}
                                            </Typography>
                                            {selectedPage?.has_bonded_data && (
                                                <>
                                                    <Typography
                                                        variant="caption"
                                                        color="warning.main"
                                                        sx={{
                                                            fontSize: 10,
                                                            mt: 2,
                                                        }}>
                                                        {selectedPage?.bind_value ??
                                                            translate(
                                                                'form.general.not_selected',
                                                            )}
                                                    </Typography>
                                                </>
                                            )}
                                        </Stack>
                                    </Card>
                                    {selectedPage?.has_bonded_data && (
                                        <>
                                            <ArrowLeftOutlined />
                                            <Stack flex={1}>
                                                <OutlinedInput
                                                    value={searchQuery}
                                                    autoComplete="off"
                                                    aria-autocomplete="none"
                                                    autoCapitalize="off"
                                                    autoCorrect="off"
                                                    startAdornment={
                                                        <InputAdornment
                                                            position="start"
                                                            sx={{ mr: -0.5 }}>
                                                            <SearchOutlined />
                                                        </InputAdornment>
                                                    }
                                                    fullWidth
                                                    onChange={event =>
                                                        setSearchQuery(
                                                            event.target.value,
                                                        )
                                                    }
                                                    inputProps={{
                                                        'aria-label': 'weight',
                                                    }}
                                                    placeholder={translate(
                                                        'static.search.placeholder',
                                                    )}
                                                />
                                                <div
                                                    ref={ref => {
                                                        if (ref) {
                                                            placeHolderRef.current =
                                                                ref;
                                                        }
                                                    }}
                                                />
                                            </Stack>
                                        </>
                                    )}
                                </Stack>
                            </Card>
                        )}
                        <Pages
                            withToolbar={false}
                            withMoveModal={false}
                            allowActions={false}
                            onPageSelect={setSelectedPage}
                            overrideLocale={locale}
                        />
                        <SearchResult
                            maxHeight="calc(50vh - 100px)"
                            anchorEl={placeHolderRef}
                            searchResults={dataChoices}
                            onItemSelected={onBindingChange}
                            normalListPlaceholder={dataChoices?.type}
                            normalListKey={varName}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="inherit"
                        variant="text"
                        onClick={handleClose}>
                        {translate('form.general.cancel')}
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={selectAndClose}>
                        {translate('form.general.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }, [open, searchQuery, selectedPage, dataChoices]);
}
